import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { motion, MotionConfig, useReducedMotion } from "framer-motion";
import { Button } from "./Button";
import { Container } from "./Container";
import { Footer } from "./Footer";
import { GridPattern } from "./GridPattern";
import { Logo, Logomark } from "./Logo";
import { Offices } from "./Offices";
import { SocialMedia } from "./SocialMedia";
import React from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import CodenraLogoLight from "../images/codenraLogoLight.svg";
import ReactGA from "react-ga4";

ReactGA.initialize("G-10RJ26GK6H");
const Layout = () => {
  return (
    <RootLayout>
      <Outlet />
    </RootLayout>
  );
};

const RootLayoutContext = createContext(null);

function XIcon(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="m5.636 4.223 14.142 14.142-1.414 1.414L4.222 5.637z" />
      <path d="M4.222 18.363 18.364 4.22l1.414 1.414L5.636 19.777z" />
    </svg>
  );
}

function MenuIcon(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M2 6h20v2H2zM2 16h20v2H2z" />
    </svg>
  );
}

function Header({
  panelId,
  icon: Icon,
  expanded,
  onToggle,
  toggleRef,
  invert = false,
}) {
  let { logoHovered, setLogoHovered } = useContext(RootLayoutContext);

  return (
    <Container>
      <div className="flex items-center justify-between">
        <Link
          to="/"
          aria-label="Home"
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
        >
          {invert ? (
            <img src={CodenraLogoLight} className="h-10 sm:h-16" />
          ) : (
            <Logo
              className="h-10 sm:h-16"
              invert={invert}
              filled={logoHovered}
            />
          )}
        </Link>
        <div className="flex items-center gap-x-8">
          <Button to="/contact" invert={invert}>
            İletişim
          </Button>
          <button
            ref={toggleRef}
            type="button"
            onClick={onToggle}
            aria-expanded={expanded ? "true" : "false"}
            aria-controls={panelId}
            className={clsx(
              "group -m-2.5 rounded-full p-2.5 transition",
              invert ? "hover:bg-white/10" : "hover:bg-neutral-950/10"
            )}
            aria-label="Toggle navigation"
          >
            <Icon
              className={clsx(
                "h-6 w-6",
                invert
                  ? "fill-white group-hover:fill-neutral-200"
                  : "fill-neutral-950 group-hover:fill-neutral-700"
              )}
            />
          </button>
        </div>
      </div>
    </Container>
  );
}

function NavigationRow({ children }) {
  return (
    <div className="even:mt-px sm:bg-neutral-950">
      <Container>
        <div className="grid grid-cols-1 sm:grid-cols-2">{children}</div>
      </Container>
    </div>
  );
}

function NavigationItem({ href, children }) {
  return (
    <Link
      to={href}
      className="group relative isolate -mx-6 bg-neutral-950 px-6 py-10 even:mt-px sm:mx-0 sm:px-0 sm:py-16 sm:odd:pr-16 sm:even:mt-0 sm:even:border-l sm:even:border-neutral-800 sm:even:pl-16"
    >
      {children}
      <span className="absolute inset-y-0 -z-10 w-screen bg-neutral-900 opacity-0 transition group-odd:right-0 group-even:left-0 group-hover:opacity-100" />
    </Link>
  );
}

function Navigation() {
  return (
    <nav className="mt-px font-display text-5xl font-medium tracking-tight text-white">
      <NavigationRow>
        <NavigationItem href="/works">Çalışmalarımız</NavigationItem>
        <NavigationItem href="/corporate">Hakkımızda</NavigationItem>
      </NavigationRow>
      <NavigationRow>
        <NavigationItem href="/services">Hizmetlerimiz</NavigationItem>
        <NavigationItem href="/process">Geliştirme Süreci</NavigationItem>
      </NavigationRow>
    </nav>
  );
}

function RootLayoutInner({ children }) {
  let panelId = useId();
  let [expanded, setExpanded] = useState(false);
  let openRef = useRef(null);
  let closeRef = useRef(null);
  let navRef = useRef(null);
  let shouldReduceMotion = useReducedMotion();
  const location = useLocation(); // Rota değişikliklerini dinler

  useEffect(() => {
    // gtag tanımlı mı kontrol et
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname,
      title: document.title,
    });
  }, [location]); // location değiştiğinde çalıştır

  useEffect(() => {
    function onClick(event) {
      if (
        event.target instanceof HTMLElement &&
        event.target.closest("a")?.href === window.location.href
      ) {
        setExpanded(false);
      }
    }

    window.addEventListener("click", onClick);

    return () => {
      window.removeEventListener("click", onClick);
    };
  }, []);

  return (
    <MotionConfig transition={shouldReduceMotion ? { duration: 0 } : undefined}>
      <header>
        <div
          className="absolute left-0 right-0 top-2 z-40 pt-14"
          aria-hidden={expanded ? "true" : undefined}
          inert={expanded ? "" : undefined}
        >
          <Header
            panelId={panelId}
            icon={MenuIcon}
            toggleRef={openRef}
            expanded={expanded}
            invert={expanded}
            onToggle={() => {
              window.scrollTo(0, 0);

              setExpanded((expanded) => !expanded);
              window.setTimeout(() =>
                closeRef.current?.focus({ preventScroll: true })
              );
            }}
          />
        </div>

        <motion.div
          layout
          id={panelId}
          style={{ height: expanded ? "auto" : "0.5rem" }}
          className="relative z-50 overflow-hidden bg-neutral-950 pt-2"
          aria-hidden={expanded ? undefined : "true"}
          inert={expanded ? undefined : ""}
        >
          <motion.div layout className="bg-neutral-800">
            <div ref={navRef} className="bg-neutral-950 pb-16 pt-14">
              <Header
                invert
                panelId={panelId}
                icon={XIcon}
                toggleRef={closeRef}
                expanded={expanded}
                onToggle={() => {
                  window.scrollTo(0, 0);

                  setExpanded((expanded) => !expanded);
                  window.setTimeout(() =>
                    openRef.current?.focus({ preventScroll: true })
                  );
                }}
              />
            </div>
            <Navigation />
            <div className="relative bg-neutral-950 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-neutral-800">
              <Container>
                <div className="grid grid-cols-1 gap-y-10 pb-16 pt-10 sm:pt-16">
                  <div>
                    <h2 className="font-display text-base font-semibold text-white">
                      Ofislerimiz
                    </h2>
                    <Offices
                      invert
                      className="my-8 grid grid-cols-1 gap-8 sm:grid-cols-3"
                    />
                  </div>
                </div>
              </Container>
            </div>
          </motion.div>
        </motion.div>
      </header>

      <motion.div
        layout
        style={{ borderTopLeftRadius: 40, borderTopRightRadius: 40 }}
        className="relative flex flex-auto overflow-hidden bg-white pt-14"
      >
        <motion.div
          layout
          className="relative isolate flex w-full flex-col pt-9"
        >
          <GridPattern
            className="absolute inset-x-0 -top-14 -z-10 h-[1000px] w-full fill-neutral-50 stroke-neutral-950/5 [mask-image:linear-gradient(to_bottom_left,white_40%,transparent_50%)]"
            yOffset={-96}
            interactive
          />

          <main className="w-full flex-auto">{children}</main>

          <Footer />
        </motion.div>
      </motion.div>
    </MotionConfig>
  );
}

export function RootLayout({ children }) {
  let location = useLocation();
  let [logoHovered, setLogoHovered] = useState(false);

  return (
    <RootLayoutContext.Provider value={{ logoHovered, setLogoHovered }}>
      <RootLayoutInner key={location.pathname}>{children}</RootLayoutInner>
    </RootLayoutContext.Provider>
  );
}

export default Layout;
